import * as React from 'react';
import { Headline4, Headline5, Headline6, BulletList, FlexColSm, FlexColMd, Flex } from 'components/Elements';
import strings from '../../../../strings/strings.json';
import styled from 'styled-components';
import useUpsertRequest from 'hooks/useUpsertRequest';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import { logError } from 'lib/debug-helpers';
import { sizes } from 'theme';
import Hint from 'components/Hint';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import { DarkFormHead, MainContainer } from './SharedQuestionnaireComponents';
import { Activity, ActivityType, BuildMethod } from 'models/Activity';
import Button from 'components/buttons/Button';

type GenerateQuestionnaireFormProps = {
    moduleId: string;
    moduleTitle: string;
    tasks: Activity[];
    returnToModulesList: Function;
    hideHeader?: boolean;
};

export default function GenerateModuleQuestionnaireForm({
    moduleId,
    moduleTitle,
    tasks: activities,
    returnToModulesList,
    hideHeader,
}: GenerateQuestionnaireFormProps): JSX.Element {
    const context = React.useContext(BuildCourseContext);
    const [error, setError] = React.useState<boolean>(false);

    const { modifyData: generateQuestionnaire, loading: generating } = useUpsertRequest(
        `modules/${moduleId}/generate-questionnaire`,
    );

    async function generate() {
        setError(false);

        const response = await generateQuestionnaire({});

        const { errors } = response;

        if (errors) {
            logError(errors);
            setError(true);
            return;
        }

        await context.refetchCourse();
        returnToModulesList();
    }

    const tasksWithContent = activities
        .filter((t) => t.type === ActivityType.TASK)
        .filter(
            (t) =>
                t.buildMethod === BuildMethod.TEXT_AND_IMAGE ||
                ((t.buildMethod === BuildMethod.VIDEO || t.buildMethod === BuildMethod.AUDIO) &&
                    t.content &&
                    t.content.length > 0),
        );
    return (
        <MainContainer>
            {!hideHeader && <DarkFormHead />}
            <Container>
                {!hideHeader && <Headline4>{strings.questionnaireGenerateForm.instruction}</Headline4>}
                <Hint hint={strings.questionnaireGenerateForm.hint} />
                <Headline5>
                    {strings.questionnaireGenerateForm.modulePrefix} {moduleTitle}
                </Headline5>
                <FlexColSm>
                    <Headline6>{strings.questionnaireGenerateForm.listHeading}</Headline6>
                    <TaskList>
                        {tasksWithContent.map((t) => (
                            <li key={t.id}>{t.title}</li>
                        ))}
                    </TaskList>
                </FlexColSm>
                <ButtonContainer>
                    {error && <ErrorNotifierSlim description={strings.questionnaireGenerateForm.generateError} />}
                    <Button
                        title={strings.questionnaireGenerateForm.submitButton}
                        aria={strings.questionnaireGenerateForm.submitButtonAria}
                        onClick={generate}
                        submitting={generating}
                    />
                </ButtonContainer>
            </Container>
        </MainContainer>
    );
}

const TaskList = styled(BulletList)`
    margin-left: ${sizes.spacingMd};
`;

const ButtonContainer = styled(Flex)`
    justify-content: end;
`;

const Container = styled(FlexColMd)`
    padding: ${sizes.spacingMd};
`;
